/**
 * Forecast Calendar - Event-driven calendar view for forecast adjustments
 * Displays promotional events, holidays, and seasonal patterns that impact demand forecasts.
 * Connects to the backend event-calendar API with fallback sample data.
 */
import React, { useState, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Button, Card, CardContent, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Slider,
  FormControlLabel, Switch, Alert, AlertTitle, Divider, Avatar,
  ToggleButton, ToggleButtonGroup, List, ListItem, ListItemText,
  ListItemIcon, InputAdornment,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  LocalOffer as PromoIcon,
  CardGiftcard as HolidayIcon,
  Celebration as SeasonalIcon,
  Campaign as MarketingIcon,
  TrendingDown as ClearanceIcon,
  Store as StoreIcon,
  TrendingUp as ImpactUpIcon,
  TrendingDown as ImpactDownIcon,
  ViewList as ListIcon,
  CalendarMonth as GridIcon,
  ChevronLeft,
  ChevronRight,
  Schedule as TimelineIcon,
  Assessment as AssessmentIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isToday,
  addMonths, subMonths, isSameMonth,
} from 'date-fns';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, Line,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

// Types
interface ForecastEvent {
  id: string;
  name: string;
  type: 'promotion' | 'holiday' | 'seasonal' | 'marketing' | 'store_opening' | 'clearance';
  start_date: string;
  end_date: string;
  stores: string[];
  brands: string[];
  categories: string[];
  expected_impact: number;
  actual_impact?: number;
  status: 'planned' | 'active' | 'completed' | 'cancelled';
  description: string;
  budget?: number;
  affected_products: number;
  forecast_adjustment: number;
  created_by: string;
}

// Event type config
const EVENT_TYPES: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  promotion: { icon: <PromoIcon />, color: '#4caf50', label: 'Promotion' },
  holiday: { icon: <HolidayIcon />, color: '#2196f3', label: 'Holiday' },
  seasonal: { icon: <SeasonalIcon />, color: '#ff9800', label: 'Seasonal' },
  marketing: { icon: <MarketingIcon />, color: '#9c27b0', label: 'Marketing' },
  clearance: { icon: <ClearanceIcon />, color: '#f44336', label: 'Clearance' },
  store_opening: { icon: <StoreIcon />, color: '#00bcd4', label: 'Store Opening' },
};

// Moroccan-context sample events (used as fallback when API has no data)
const SAMPLE_EVENTS: ForecastEvent[] = [
  {
    id: '1', name: 'Ramadan Promotion', type: 'seasonal',
    start_date: '2026-02-17', end_date: '2026-03-19',
    stores: ['Morocco Mall', 'Anfa Place', 'Mega Mall Rabat'],
    brands: ['Dior', 'Chanel', 'YSL'], categories: ['Fragrance', 'Makeup'],
    expected_impact: 35, actual_impact: 42, status: 'active',
    description: 'Annual Ramadan promotion with special gift sets and bundles',
    budget: 150000, affected_products: 450, forecast_adjustment: 35, created_by: 'Karim El Amrani',
  },
  {
    id: '2', name: "Valentine's Day Campaign", type: 'holiday',
    start_date: '2026-02-01', end_date: '2026-02-14',
    stores: ['All Stores'], brands: ['All Brands'], categories: ['Fragrance'],
    expected_impact: 28, status: 'completed',
    description: 'Valentine special offers on premium fragrances',
    budget: 80000, affected_products: 230, forecast_adjustment: 28, created_by: 'Nadia Bennis',
  },
  {
    id: '3', name: 'Soldes de Printemps', type: 'promotion',
    start_date: '2026-03-20', end_date: '2026-04-15',
    stores: ['Morocco Mall', 'Mega Mall Rabat', 'Tanger City Mall'],
    brands: ['Lancôme', 'Estée Lauder'], categories: ['Skincare', 'Makeup'],
    expected_impact: 22, status: 'planned',
    description: 'Spring sale on skincare and makeup collections',
    budget: 60000, affected_products: 310, forecast_adjustment: 22, created_by: 'Youssef Tazi',
  },
  {
    id: '4', name: "Fête des Mères", type: 'holiday',
    start_date: '2026-05-25', end_date: '2026-05-31',
    stores: ['All Stores'], brands: ['Dior', 'Guerlain', 'Hermès'], categories: ['Fragrance', 'Skincare'],
    expected_impact: 30, status: 'planned',
    description: "Mother's Day premium gift sets promotion",
    budget: 70000, affected_products: 200, forecast_adjustment: 30, created_by: 'Karim El Amrani',
  },
  {
    id: '5', name: 'Summer Clearance', type: 'clearance',
    start_date: '2026-07-01', end_date: '2026-07-31',
    stores: ['Morocco Mall', 'Borj Fez'], brands: ['Estée Lauder', 'Lancôme'],
    categories: ['Skincare', 'Makeup'], expected_impact: -15, status: 'planned',
    description: 'End of season clearance sale on spring/summer collections',
    budget: 40000, affected_products: 180, forecast_adjustment: -15, created_by: 'Nadia Bennis',
  },
  {
    id: '6', name: 'Rentrée Scolaire', type: 'seasonal',
    start_date: '2026-09-01', end_date: '2026-09-15',
    stores: ['All Stores'], brands: ['All Brands'], categories: ['Skincare'],
    expected_impact: 18, status: 'planned',
    description: 'Back-to-school season skincare essentials promotion',
    budget: 45000, affected_products: 150, forecast_adjustment: 18, created_by: 'Youssef Tazi',
  },
  {
    id: '7', name: 'New Flagship Casablanca', type: 'store_opening',
    start_date: '2026-04-01', end_date: '2026-04-07',
    stores: ['Anfa Place'], brands: ['All Brands'], categories: ['All Categories'],
    expected_impact: 40, status: 'planned',
    description: 'Grand opening of new flagship counter at Anfa Place with exclusive launches',
    budget: 120000, affected_products: 500, forecast_adjustment: 40, created_by: 'Karim El Amrani',
  },
];

const ForecastCalendar: React.FC = () => {
  const { fmtCurrency, fmtCurrencyLabel } = useCurrencyFormat();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list' | 'timeline'>('calendar');
  const [selectedEvent, setSelectedEvent] = useState<ForecastEvent | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    name: '', type: 'promotion', start_date: new Date(), end_date: new Date(),
    expected_impact: 0, description: '', budget: 0, auto_adjust: true,
  });

  // Try loading events from API; fall back to sample data
  const { data: apiEvents } = useQuery({
    queryKey: ['forecast-calendar-events', currentMonth.getFullYear()],
    queryFn: async () => {
      const res = await api.getEventCalendar(currentMonth.getFullYear());
      return res.data;
    },
    retry: false,
  });

  const events: ForecastEvent[] = useMemo(() => {
    if (apiEvents?.events && apiEvents.events.length > 0) return apiEvents.events;
    return SAMPLE_EVENTS;
  }, [apiEvents]);

  // Metrics
  const metrics = useMemo(() => {
    const active = events.filter(e => e.status === 'active').length;
    const upcoming = events.filter(e => e.status === 'planned').length;
    const avgImpact = events.length > 0
      ? events.reduce((s, e) => s + (e.actual_impact ?? e.expected_impact), 0) / events.length
      : 0;
    const totalProducts = events.reduce((s, e) => s + e.affected_products, 0);
    return { total: events.length, active, upcoming, avgImpact, totalProducts };
  }, [events]);

  // Calendar helpers
  const daysInMonth = useMemo(() => {
    return eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });
  }, [currentMonth]);

  const getEventsForDay = (day: Date) =>
    events.filter(e => {
      const s = new Date(e.start_date);
      const en = new Date(e.end_date);
      return day >= s && day <= en;
    });

  const eventsThisMonth = useMemo(() =>
    events.filter(e => {
      const s = new Date(e.start_date);
      const en = new Date(e.end_date);
      return isSameMonth(s, currentMonth) || isSameMonth(en, currentMonth) ||
        (s < startOfMonth(currentMonth) && en > endOfMonth(currentMonth));
    }), [events, currentMonth]);

  const openDetails = (event: ForecastEvent) => {
    setSelectedEvent(event);
    setDetailsOpen(true);
  };

  const handleCreate = () => {
    setCreateOpen(false);
    // In production, POST to /api/v1/event-calendar/events
  };

  // Timeline data
  const timelineData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((m, i) => {
      const monthEvents = events.filter(e => {
        const s = new Date(e.start_date);
        const en = new Date(e.end_date);
        return s.getMonth() <= i && en.getMonth() >= i;
      });
      return {
        month: m,
        events: monthEvents.length,
        impact: monthEvents.reduce((s, e) => s + (e.actual_impact ?? e.expected_impact), 0),
        products: monthEvents.reduce((s, e) => s + e.affected_products, 0),
      };
    });
  }, [events]);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4">Forecast Calendar</Typography>
            <Typography variant="body2" color="text.secondary">
              Events, promotions, and seasonal patterns affecting demand forecasts
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} size="small">Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Create Event
          </Button>
        </Box>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Total Events</Typography>
              <Typography variant="h4">{metrics.total}</Typography>
              <Typography variant="caption" color="text.secondary">This year</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ borderLeft: '4px solid #4caf50' }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Active Now</Typography>
              <Typography variant="h4" color="success.main">{metrics.active}</Typography>
              <Typography variant="caption" color="text.secondary">Running campaigns</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ borderLeft: '4px solid #2196f3' }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Upcoming</Typography>
              <Typography variant="h4" color="info.main">{metrics.upcoming}</Typography>
              <Typography variant="caption" color="text.secondary">Planned events</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Avg Impact</Typography>
              <Typography variant="h4">+{metrics.avgImpact.toFixed(0)}%</Typography>
              <Typography variant="caption" color="text.secondary">On demand forecast</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ background: 'linear-gradient(45deg, #8B2438, #42a5f5)' }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" color="white">SKUs Affected</Typography>
              <Typography variant="h4" color="white">{metrics.totalProducts.toLocaleString()}</Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.7)">Across all events</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* View Controls */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="calendar"><GridIcon sx={{ mr: 0.5 }} /> Calendar</ToggleButton>
          <ToggleButton value="list"><ListIcon sx={{ mr: 0.5 }} /> List</ToggleButton>
          <ToggleButton value="timeline"><TimelineIcon sx={{ mr: 0.5 }} /> Timeline</ToggleButton>
        </ToggleButtonGroup>

        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 160, textAlign: 'center' }}>
            {format(currentMonth, 'MMMM yyyy')}
          </Typography>
          <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} size="small">
            <ChevronRight />
          </IconButton>
          <Button size="small" onClick={() => setCurrentMonth(new Date())}>Today</Button>
        </Box>
      </Paper>

      {/* Calendar View — Outlook-style monthly grid */}
      {viewMode === 'calendar' && (() => {
        const leadingBlanks = getDay(daysInMonth[0]);
        const totalCells = leadingBlanks + daysInMonth.length;
        const trailingBlanks = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
        const rows = (totalCells + trailingBlanks) / 7;

        return (
          <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
            {/* Day-of-week header row */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
              bgcolor: 'var(--color-bg-secondary)', borderBottom: '1px solid var(--color-border)',
            }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <Box key={d} sx={{
                  py: 1, textAlign: 'center',
                  borderRight: '1px solid var(--color-border)',
                  '&:last-child': { borderRight: 'none' },
                }}>
                  <Typography sx={{
                    fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)',
                  }}>{d}</Typography>
                </Box>
              ))}
            </Box>

            {/* Calendar grid rows */}
            {Array.from({ length: rows }).map((_, rowIdx) => (
              <Box key={rowIdx} sx={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                minHeight: 110,
                borderBottom: rowIdx < rows - 1 ? '1px solid var(--color-border)' : 'none',
              }}>
                {Array.from({ length: 7 }).map((_, colIdx) => {
                  const cellIdx = rowIdx * 7 + colIdx;
                  const dayIdx = cellIdx - leadingBlanks;
                  const isValidDay = dayIdx >= 0 && dayIdx < daysInMonth.length;
                  const day = isValidDay ? daysInMonth[dayIdx] : null;
                  const dayEvents = day ? getEventsForDay(day) : [];
                  const current = day ? isToday(day) : false;
                  const isWeekend = colIdx === 0 || colIdx === 6;

                  return (
                    <Box key={colIdx} sx={{
                      borderRight: colIdx < 6 ? '1px solid var(--color-border)' : 'none',
                      bgcolor: !isValidDay ? 'var(--color-bg-secondary)' : isWeekend ? 'rgba(0,0,0,0.015)' : 'var(--color-surface)',
                      display: 'flex', flexDirection: 'column',
                      opacity: isValidDay ? 1 : 0.4,
                      transition: 'background-color var(--transition-fast)',
                      '&:hover': isValidDay ? { bgcolor: 'var(--color-bg-secondary)' } : {},
                    }}>
                      {/* Day number */}
                      <Box sx={{ px: 1, pt: 0.5, pb: 0.25, display: 'flex', justifyContent: 'flex-end' }}>
                        {isValidDay && (
                          <Typography sx={{
                            fontSize: 'var(--text-sm)', fontWeight: current ? 700 : 400,
                            color: current ? 'var(--color-surface)' : 'var(--color-text-primary)',
                            bgcolor: current ? '#8B2438' : 'transparent',
                            width: 26, height: 26, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontVariantNumeric: 'tabular-nums',
                          }}>
                            {format(day!, 'd')}
                          </Typography>
                        )}
                      </Box>

                      {/* Event bars */}
                      <Box sx={{ flex: 1, px: 0.5, pb: 0.5, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {dayEvents.slice(0, 3).map(ev => (
                          <Box
                            key={ev.id}
                            onClick={() => openDetails(ev)}
                            sx={{
                              px: 0.75, py: '1px',
                              bgcolor: `${EVENT_TYPES[ev.type]?.color || '#757575'}18`,
                              borderLeft: `3px solid ${EVENT_TYPES[ev.type]?.color || '#757575'}`,
                              borderRadius: '2px',
                              cursor: 'pointer',
                              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                              transition: 'background-color var(--transition-fast)',
                              '&:hover': { bgcolor: `${EVENT_TYPES[ev.type]?.color || '#757575'}30` },
                            }}
                          >
                            <Typography sx={{
                              fontSize: '0.65rem', fontWeight: 500, lineHeight: 1.4,
                              color: EVENT_TYPES[ev.type]?.color || 'var(--color-text-primary)',
                              overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                              {ev.name}
                            </Typography>
                          </Box>
                        ))}
                        {dayEvents.length > 3 && (
                          <Typography sx={{
                            fontSize: '0.6rem', color: 'var(--color-text-muted)',
                            px: 0.75, cursor: 'pointer', fontWeight: 500,
                          }}>
                            +{dayEvents.length - 3} more
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}

            {/* Month events legend */}
            {eventsThisMonth.length > 0 && (
              <Box sx={{ p: 1.5, borderTop: '1px solid var(--color-border)', bgcolor: 'var(--color-bg-secondary)', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', mr: 1 }}>
                  THIS MONTH:
                </Typography>
                {eventsThisMonth.map(ev => (
                  <Box
                    key={ev.id}
                    onClick={() => openDetails(ev)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      px: 1, py: 0.25, borderRadius: 'var(--radius-sm)',
                      bgcolor: `${EVENT_TYPES[ev.type]?.color || '#757575'}15`,
                      borderLeft: `3px solid ${EVENT_TYPES[ev.type]?.color || '#757575'}`,
                      cursor: 'pointer', transition: 'background-color var(--transition-fast)',
                      '&:hover': { bgcolor: `${EVENT_TYPES[ev.type]?.color || '#757575'}28` },
                    }}
                  >
                    <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                      {ev.name}
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: ev.expected_impact > 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                      {ev.expected_impact > 0 ? '+' : ''}{ev.expected_impact}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      })()}

      {/* List View */}
      {viewMode === 'list' && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>All Events</Typography>
          <List>
            {events.map(ev => (
              <React.Fragment key={ev.id}>
                <ListItem
                  secondaryAction={
                    <Box display="flex" gap={1} alignItems="center">
                      <Chip
                        label={ev.status}
                        size="small"
                        color={ev.status === 'active' ? 'success' : ev.status === 'planned' ? 'info' : ev.status === 'completed' ? 'default' : 'error'}
                      />
                      <Chip
                        icon={ev.expected_impact > 0 ? <ImpactUpIcon /> : <ImpactDownIcon />}
                        label={`${ev.expected_impact > 0 ? '+' : ''}${ev.expected_impact}%`}
                        size="small"
                        color={ev.expected_impact > 0 ? 'success' : 'error'}
                      />
                      <IconButton size="small" onClick={() => openDetails(ev)}>
                        <AssessmentIcon />
                      </IconButton>
                    </Box>
                  }
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openDetails(ev)}
                >
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: EVENT_TYPES[ev.type]?.color || '#757575' }}>
                      {EVENT_TYPES[ev.type]?.icon}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={ev.name}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(ev.start_date), 'MMM dd')} – {format(new Date(ev.end_date), 'MMM dd, yyyy')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(ev.stores || []).join(', ')} | {ev.affected_products} SKUs | {(ev.categories || []).join(', ')}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Event Impact Timeline</Typography>
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <RechartsTooltip />
              <Legend />
              <Area yAxisId="left" type="monotone" dataKey="events" stroke="#8B2438"
                fill="#8B2438" fillOpacity={0.3} name="Event Count" />
              <Area yAxisId="left" type="monotone" dataKey="impact" stroke="#C9A96E"
                fill="#C9A96E" fillOpacity={0.3} name="Total Impact %" />
              <Line yAxisId="right" type="monotone" dataKey="products" stroke="#ff7300"
                name="Products Affected" />
            </AreaChart>
          </ResponsiveContainer>

          {/* Event type distribution */}
          <Box mt={3}>
            <Typography variant="subtitle1" gutterBottom>By Event Type</Typography>
            <Grid container spacing={2}>
              {Object.entries(EVENT_TYPES).map(([type, config]) => {
                const count = events.filter(e => e.type === type).length;
                if (count === 0) return null;
                return (
                  <Grid item xs={6} md={2} key={type}>
                    <Card variant="outlined" sx={{ borderLeft: `4px solid ${config.color}` }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ bgcolor: config.color, width: 28, height: 28 }}>
                            {config.icon}
                          </Avatar>
                          <Box>
                            <Typography variant="h6">{count}</Typography>
                            <Typography variant="caption" color="text.secondary">{config.label}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Paper>
      )}

      {/* Event Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedEvent?.name}</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Alert severity={selectedEvent.expected_impact > 0 ? 'success' : 'warning'}>
                  <AlertTitle>Forecast Impact</AlertTitle>
                  Expected demand change of {selectedEvent.expected_impact > 0 ? '+' : ''}{selectedEvent.expected_impact}%
                  on {selectedEvent.affected_products} SKUs
                  {selectedEvent.actual_impact != null && (
                    <Typography variant="body2">
                      Actual impact: {selectedEvent.actual_impact > 0 ? '+' : ''}{selectedEvent.actual_impact}%
                    </Typography>
                  )}
                </Alert>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Box>
                  <Chip
                    icon={EVENT_TYPES[selectedEvent.type]?.icon as React.ReactElement}
                    label={EVENT_TYPES[selectedEvent.type]?.label}
                    sx={{ bgcolor: `${EVENT_TYPES[selectedEvent.type]?.color}20` }}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Event Status</Typography>
                <Box>
                  <Chip
                    label={selectedEvent.status}
                    color={selectedEvent.status === 'active' ? 'success' : selectedEvent.status === 'planned' ? 'info' : 'default'}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography variant="body2">
                  {format(new Date(selectedEvent.start_date), 'MMM dd')} – {format(new Date(selectedEvent.end_date), 'MMM dd, yyyy')}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Budget</Typography>
                <Typography variant="body2">
                  {selectedEvent.budget ? fmtCurrency(selectedEvent.budget) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Stores</Typography>
                <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                  {(selectedEvent.stores || []).map(s => (
                    <Chip key={s} label={s} size="small" icon={<LocationIcon />} />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Brands</Typography>
                <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                  {(selectedEvent.brands || []).map(b => <Chip key={b} label={b} size="small" />)}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Categories</Typography>
                <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                  {(selectedEvent.categories || []).map(c => <Chip key={c} label={c} size="small" />)}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body2">{selectedEvent.description}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Created by</Typography>
                <Typography variant="body2">{selectedEvent.created_by}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained">Apply to Forecasts</Button>
        </DialogActions>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Forecast Event</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Event Name" value={eventForm.name}
                onChange={e => setEventForm({ ...eventForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select value={eventForm.type} label="Event Type"
                  onChange={e => setEventForm({ ...eventForm, type: e.target.value })}>
                  {Object.entries(EVENT_TYPES).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Budget" type="number" value={eventForm.budget}
                onChange={e => setEventForm({ ...eventForm, budget: Number(e.target.value) })}
                InputProps={{ startAdornment: <InputAdornment position="start">{fmtCurrencyLabel()}</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker label="Start Date" value={eventForm.start_date}
                  onChange={d => setEventForm({ ...eventForm, start_date: d || new Date() })}
                  slotProps={{ textField: { fullWidth: true } }} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker label="End Date" value={eventForm.end_date}
                  onChange={d => setEventForm({ ...eventForm, end_date: d || new Date() })}
                  slotProps={{ textField: { fullWidth: true } }} />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <Typography gutterBottom>Expected Forecast Impact: {eventForm.expected_impact}%</Typography>
              <Slider value={eventForm.expected_impact}
                onChange={(_, v) => setEventForm({ ...eventForm, expected_impact: v as number })}
                min={-50} max={100}
                marks={[{ value: -50, label: '-50%' }, { value: 0, label: '0%' }, { value: 50, label: '+50%' }, { value: 100, label: '+100%' }]}
                valueLabelDisplay="auto" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Description" value={eventForm.description}
                onChange={e => setEventForm({ ...eventForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={eventForm.auto_adjust}
                  onChange={e => setEventForm({ ...eventForm, auto_adjust: e.target.checked })} />}
                label="Automatically adjust demand forecasts based on expected impact" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create Event</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ForecastCalendar;
